import { Link } from "@tanstack/react-router";
import { Calendar, MapPin, Tag } from "lucide-react";
import { formatPrice, formatDate } from "@/lib/mock-data";
import { computeDisplayPrice, usePlatformFeeConfig } from "@/lib/fees";
import { getPaymentProviderSearch } from "@/lib/payment-provider";
import { CldImage } from "@/components/CldImage";
import type { Tables } from "@/integrations/supabase/types";

type EventWithTicketTypes = Tables<"events"> & {
  ticket_types: Tables<"ticket_types">[];
};

export function EventCard({ event }: { event: EventWithTicketTypes }) {
  const feeCfg = usePlatformFeeConfig();
  const providerSearch = getPaymentProviderSearch();
  const minBasePrice = event.ticket_types.length > 0
    ? Math.min(...event.ticket_types.map((t) => t.price))
    : 0;
  const minPrice = computeDisplayPrice(minBasePrice, feeCfg);
  const hasAvailable = event.ticket_types.some((t) => t.available > 0);

  return (
    <Link
      to="/events/$eventId"
      params={{ eventId: event.id }}
      search={providerSearch as any}
      className="group block overflow-hidden rounded-2xl bg-card border border-border card-hover"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <CldImage
          src={event.image_url}
          fallbackSrc="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800&q=80"
          width={800}
          alt={event.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent" />
        <span className="absolute top-3 left-3 rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
          {event.category}
        </span>
        {!hasAvailable && (
          <span className="absolute top-3 right-3 rounded-full bg-destructive px-3 py-1 text-xs font-semibold text-destructive-foreground">
            Agotado
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-base font-bold text-card-foreground leading-tight line-clamp-1 group-hover:text-primary transition-colors">
          {event.title}
        </h3>
        <div className="mt-2 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            <span>{formatDate(event.date)} · {event.time}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
            <span className="line-clamp-1">{event.location}, {event.city}</span>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-primary">
            <Tag className="h-3.5 w-3.5" />
            <span className="text-sm font-bold">Desde {formatPrice(minPrice)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
